import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import './CustomDropdown.css';

function CustomDropdown({ options, value, onChange, placeholder }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getDisplayValue = useCallback((option) => {
    if (typeof option === 'string') return option;
    if (option && typeof option === 'object') return option.displayName || option.name_en || option.name_kn || '';
    return '';
  }, []);

  const getOptionId = useCallback((option) => {
    if (typeof option === 'string') return option;
    if (option && typeof option === 'object') return option.id || option;
    return option;
  }, []);

  const isSelected = useCallback((option) => {
    if (typeof value === 'string' && typeof option === 'string') return option === value;
    if (value && typeof value === 'object' && option && typeof option === 'object') {
      return (value.id || value) === (option.id || option);
    }
    return false;
  }, [value]);

  const selectedOption = useMemo(() => 
    options.find(opt => {
      if (typeof value === 'string' && typeof opt === 'string') return opt === value;
      if (value && typeof value === 'object' && opt && typeof opt === 'object') {
        return (value.id || value) === (opt.id || opt);
      }
      return false;
    }) || value,
    [options, value]
  );

  const displayValue = useMemo(() => getDisplayValue(selectedOption), [selectedOption, getDisplayValue]);

  const handleSelect = useCallback((option) => {
    onChange(option);
    setIsOpen(false);
  }, [onChange]);

  return (
    <div className="custom-dropdown-container" ref={dropdownRef}>
      <div 
        className={`custom-dropdown-field ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{displayValue || placeholder || 'Select...'}</span>
        <span className="arrow-icon">▼</span>
      </div>
      {isOpen && (
        <div className="custom-dropdown-menu">
          {options.map((option, index) => {
            const optionDisplay = getDisplayValue(option);
            const selected = isSelected(option);
            return (
              <div
                key={getOptionId(option) || index}
                className={`custom-dropdown-item ${selected ? 'selected' : ''}`}
                onClick={() => handleSelect(option)}
              >
                {selected ? (
                  <span className="checkmark">✓</span>
                ) : (
                  <span className="spacer"></span>
                )}
                <span>{optionDisplay}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default CustomDropdown;
